import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getUnreadCount, listNotifications, type UserNotification } from "@ando/core";

/**
 * System notifications (a Hive Flow paused for approval, a worker failed,
 * ...) — written via ``NotificationDelivery.create()`` on the BE, distinct
 * from the proactive "nudges" the header bell already shows (see
 * ``useNudgeInbox.ts``). Until #922 these had no frontend surface at all.
 * ``NotificationPill`` merges both feeds into one popover.
 */

export const NOTIFICATIONS_UNREAD_QUERY_KEY = ["notifications", "unread-count"] as const;
export const NOTIFICATIONS_INBOX_QUERY_KEY = ["notifications", "inbox"] as const;
const NOTIFICATIONS_BADGE_POLL_MS = 5 * 60_000;
const NOTIFICATIONS_INBOX_STALE_MS = 30_000;

export interface UseNotificationsInboxOptions {
  readonly limit?: number;
  readonly enabled?: boolean;
}

export function useNotificationsUnreadCount(): UseQueryResult<{ unread_count: number }, Error> {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_QUERY_KEY,
    queryFn: () => getUnreadCount(),
    staleTime: 60_000,
    refetchInterval: NOTIFICATIONS_BADGE_POLL_MS,
    retry: 1,
  });
}

export function useNotificationsInbox(
  opts: UseNotificationsInboxOptions = {},
): UseQueryResult<readonly UserNotification[], Error> {
  const limit = opts.limit ?? 8;
  return useQuery({
    queryKey: [...NOTIFICATIONS_INBOX_QUERY_KEY, { limit }],
    // Fetches a wider unread-first page then trims client-side: the BE
    // list endpoint has no ``unread_only`` filter wired to this client yet,
    // so ask for enough rows that unread ones aren't pushed off by read ones.
    queryFn: async () => {
      const res = await listNotifications({ pageSize: 20 });
      return res.items.filter((n) => !n.is_read).slice(0, limit);
    },
    enabled: opts.enabled ?? true,
    staleTime: NOTIFICATIONS_INBOX_STALE_MS,
    retry: 1,
  });
}
